import 'focus-visible';
import { PrismicProvider } from '@prismicio/react';
import { GlobalStyles, theme } from '@remote-com/norma/foundations';
import * as Sentry from '@sentry/nextjs';
import App from 'next/app';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { CookieScripts } from 'remote-com-shared/components/CookieScripts';
import { DocumentHead } from 'remote-com-shared/components/DocumentHead';
import { LocaleContextProvider } from 'remote-com-shared/domains/locales/context';
import { LocalisedContentProvider } from 'remote-com-shared/domains/localisedContent/context';
import { ThemeProvider } from 'styled-components';
import DefaultLayout from '@/layouts/Default';
import { RudderStackInitializer } from '@/src/domains/rudderstack/RudderStackInitializer';
import { GlobalHooks } from '@/src/domains/shared/hooks/GlobalHooks';
import { linkResolver } from '@/src/domains/shared/url';
import GlobalStyle from '@/src/style/global';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// unfortunately is needed because of stale foundations (eg brandPrimary) 😭
// import dragonTheme from '@remote-com/mushu/src/style/theme/index.js';

const pandaTheme = {
  // ...dragonTheme,
  ...theme,
};

class Panda extends App {
  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });

      Sentry.captureException(error);
    });
  }

  render() {
    const { Layout, Component, pageProps } = this.props;
    const { locale, cookieControlConfig, localisedContent } = pageProps.sharedServices || {};
    const ComponentLayout = Component.Layout || Layout || DefaultLayout;

    return (
      <LocaleContextProvider value={locale}>
        <LocalisedContentProvider value={localisedContent}>
          <ThemeProvider theme={pandaTheme}>
            <QueryClientProvider client={queryClient}>
              <PrismicProvider linkResolver={linkResolver}>
                <DocumentHead />
                <GlobalStyles />
                <GlobalStyle />
                <GlobalHooks />
                <RudderStackInitializer />
                <ComponentLayout {...pageProps}>
                  <Component {...pageProps} />
                  <CookieScripts {...cookieControlConfig} />
                </ComponentLayout>
                <ReactQueryDevtools initialIsOpen={false} />
              </PrismicProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </LocalisedContentProvider>
      </LocaleContextProvider>
    );
  }
}

export default Panda;
